import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useGameStatusStore } from 'modules/Room/game-status/store/game-status-store';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { InfoBannerType } from 'shared/components/ui/InfoBanner/InfoBanner';
import { MoveStatusRenderer } from 'shared/components/ui/MoveStatusRenderer/MoveStatusRenderer';
import { MoveStatusType } from 'shared/components/ui/MoveStatusRenderer/MoveStatusRenderer.types';
import { RoundOutcome } from 'shared/constants/shared-enums';
import { useRoomStore } from 'shared/store/roomStore';

const ROUND_NUMBER_FIRST = 1;

export const useWaitYourMoveGameStatusText = (
  isSuccess: boolean,
  showTimer: boolean,
  showCloseButton: boolean,
  technicalDefeatError: boolean,
  roundOutcome: RoundOutcome,
) => {
  const { playMoveGameStatus } = useGameStatusStore();

  const { roundCounter } = useRoomStore();
  const { t } = useTranslation();

  const FirstRound = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.tip')}
      />
    ),
    [t],
  );

  const FirstRoundWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.timerTip')}
        type={InfoBannerType.WARNING}
        // hideIconOnMobile
      />
    ),
    [t],
  );

  const NoneState = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.select')}
        // hideIconOnMobile
      />
    ),
    [t],
  );

  const YouWin = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.tip')}
        type={InfoBannerType.INFO}
      />
    ),
    [t],
  );

  const YouLose = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.tip')}
      />
    ),
    [t],
  );

  const Draw = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.tip')}
      />
    ),
    [t],
  );

  const NoneStateWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.timerSelect')}
        // hideIconOnMobile
      />
    ),
    [t],
  );

  const YouWinWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.timerSelect')}
        type={InfoBannerType.WARNING}
        // hideIconOnMobile
      />
    ),
    [t],
  );

  const YouLoseWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.timerSelect')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const DrawWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.timerSelect')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const ErrorText = useCallback(
    () => (
      <MoveStatusRenderer
        title={t('modals.waitYourMove.chooseAgain')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const ErrorTextWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.timerChooseAgain')}
        type={InfoBannerType.WARNING}
        // hideIconOnMobile
      />
    ),
    [t],
  );

  const RejectText = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.please')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const RejectTextWithTimer = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.timerChooseAgain')}
        type={InfoBannerType.WARNING}
        // hideIconOnMobile
      />
    ),
    [t],
  );

  const TechnicalDefeatAllStates = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.technicalDefeatExplanation')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const TechnicalDefeatAllStatesError = useCallback(
    () => (
      <MoveStatusRenderer
        statusIcon={MoveStatusType.WAITING_FOR_YOUR_MOVE}
        title={t('modals.waitYourMove.technicalDefeatErrorExplanation')}
        type={InfoBannerType.WARNING}
      />
    ),
    [t],
  );

  const notFirstRoundTextGenerator = useCallback(() => {
    if (roundOutcome === RoundOutcome.None) return <NoneState />;
    if (roundOutcome === RoundOutcome.Draw) return <Draw />;
    if (roundOutcome === RoundOutcome.Win) return <YouWin />;
    if (roundOutcome === RoundOutcome.Lose) return <YouLose />;
  }, [roundOutcome]);
  const notFirstRoundTextGeneratorWithTimer = useCallback(() => {
    if (roundOutcome === RoundOutcome.None) return <NoneStateWithTimer />;
    if (roundOutcome === RoundOutcome.Draw) return <DrawWithTimer />;
    if (roundOutcome === RoundOutcome.Win) return <YouWinWithTimer />;
    if (roundOutcome === RoundOutcome.Lose) return <YouLoseWithTimer />;
  }, [roundOutcome]);

  const getTextComponent = useCallback(
    () => (
      <>
        <AnimatedTransition
          show={
            !playMoveGameStatus.error &&
            !playMoveGameStatus.reject &&
            !showCloseButton &&
            roundCounter !== 1 &&
            showTimer
          }
        >
          {notFirstRoundTextGeneratorWithTimer()}
        </AnimatedTransition>
        <AnimatedTransition
          show={
            !playMoveGameStatus.error &&
            !playMoveGameStatus.reject &&
            !showCloseButton &&
            roundCounter === ROUND_NUMBER_FIRST &&
            !showTimer
          }
        >
          <FirstRound />
        </AnimatedTransition>
        <AnimatedTransition
          show={
            !playMoveGameStatus.error &&
            !playMoveGameStatus.reject &&
            !showCloseButton &&
            roundCounter === ROUND_NUMBER_FIRST &&
            showTimer
          }
        >
          <FirstRoundWithTimer />
        </AnimatedTransition>
        <AnimatedTransition
          show={
            !playMoveGameStatus.error &&
            !playMoveGameStatus.reject &&
            !showCloseButton &&
            roundCounter !== ROUND_NUMBER_FIRST &&
            !showTimer
          }
        >
          {notFirstRoundTextGenerator()}
        </AnimatedTransition>
        <AnimatedTransition
          show={playMoveGameStatus.error && !isSuccess && !showCloseButton && showTimer}
        >
          <ErrorTextWithTimer />
        </AnimatedTransition>
        <AnimatedTransition
          show={playMoveGameStatus.error && !isSuccess && !showCloseButton && !showTimer}
        >
          <ErrorText />
        </AnimatedTransition>
        <AnimatedTransition
          show={playMoveGameStatus.reject && !isSuccess && !showCloseButton && showTimer}
        >
          <RejectTextWithTimer />
        </AnimatedTransition>
        <AnimatedTransition
          show={playMoveGameStatus.reject && !isSuccess && !showCloseButton && !showTimer}
        >
          <RejectText />
        </AnimatedTransition>
        <AnimatedTransition show={showCloseButton && technicalDefeatError}>
          <TechnicalDefeatAllStatesError />
        </AnimatedTransition>
        <AnimatedTransition show={showCloseButton && !technicalDefeatError}>
          <TechnicalDefeatAllStates />
        </AnimatedTransition>
      </>
    ),
    [
      playMoveGameStatus,
      isSuccess,
      showCloseButton,
      roundCounter,
      showTimer,
      notFirstRoundTextGeneratorWithTimer,
      notFirstRoundTextGenerator,
      FirstRound,
      FirstRoundWithTimer,
      ErrorTextWithTimer,
      ErrorText,
      RejectTextWithTimer,
      RejectText,
      TechnicalDefeatAllStatesError,
      TechnicalDefeatAllStates,
    ],
  );

  return { getTextComponent };
};
